import { markRaw, reactive } from 'vue';
import moment from 'moment';
import ChecklistService from '@/checklist/services/ChecklistService';
import DossierContactService from '@/contact/services/DossierContactService';
import DmsClient from '@/general/client/DmsClient';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import AuthService from '@/general/services/AuthService';
import MediaFileService from '@/general/services/MediaFileService';
import MediaFileService2 from '@/general/services/MediaFileService2';
import pushService, { Unbinder } from '@/general/services/PushService';
import ReferenceService from '@/general/services/ReferenceService';
import JobClient from '@/jobs/client/JobClient';
import { parseJob, parseSignature } from '@/jobs/converters/jobConverters';
import Job from '@/jobs/models/Job';
import JobService from '@/jobs/services/JobService';
import SurveyService from '@/satisfaction/services/SurveyService';
import TagType from '@/shared/constants/TagType';
import AssignmentData from '@/shared/models/AssignmentData';
import Tag from '@/shared/models/Tag';
import serviceEvents, { DmsImportFinished, DossierWorkStatusChanged, JobStatusUpdated, JobUpdated, WorkPreparationStatusChanged } from '@/shared/services/events/serviceEvents';
import { isPresent, present } from '@/shared/utils/typescript';
import TaskService from '@/tasks/services/TaskService';
import TireClient from '@/tires/client/TireClient';
import TiresSummary from '@/tires/models/TiresSummary';
import { parseVehicle } from '@/vehicle/converters/vehicleConverters';
import Vehicle from '@/vehicle/models/Vehicle';
import WorkPreparationStatus from '@/workPreparation/constants/WorkPreparationStatus';
import WorkPreparationService from '@/workPreparation/services/WorkPreparationService';
import DossierClient, { WorkStatusData } from '../client/DossierClient';
import QualityControlClient from '../client/QualityControlClient';
import VehicleStatus, { VEHICLE_STATUS_TRANSITIONS } from '../constants/VehicleStatus';
import WarrantyStatus from '../constants/WarrantyStatus';
import WorkStatus, { WORK_STATUS_TRANSITIONS } from '../constants/WorkStatus';
import { parseDossier } from '../converters/dossierConverters';
import { CleaningData, DossierId } from '../models/AbstractDossier';
import { CheckInData, CheckInExtraJob } from '../models/CheckInData';
import Dossier, { AppointmentData } from '../models/Dossier';
import DossierContext from '../models/DossierContext';
import DossierDetailContext from '../models/DossierDetailContext';
import PaymentRequest from '../models/PaymentRequest';
import PaymentRequestContext from '../models/PaymentRequestContext';
import { PickedUpData } from '../models/PickedUpData';
import { ReadyForPickupServiceData } from '../models/ReadyForPickupData';
import DmsService from './DmsService';
import DossierVehicleService from './DossierVehicleService';
import FluidService from './FluidService';
import PaymentRequestService from './PaymentRequestService';
import TestDriveService from './TestDriveService';
import Location from '@/vehicle/models/Location';
import DriveBookingService from '@/driveBookings/services/DriveBookingService';
import PlannerDossierDetailsContext from '@/planner/models/PlannerDossierDetailsContext';

class DossierService {
  async loadForPlanner(id: DossierId): Promise<PlannerDossierDetailsContext> {
    const dossier = reactive(await this.doLoad(id)) as Dossier;
    const mediaContext = await MediaFileService2.loadForDossier(dossier.id);
    const contactContext = await DossierContactService.loadAllForDossier(id);
    const driveBookingContext = await DriveBookingService.loadForDossier(contactContext, dossier);

    return reactive(new PlannerDossierDetailsContext(dossier, driveBookingContext, mediaContext)) as PlannerDossierDetailsContext;
  }

  async load(id: DossierId): Promise<DossierContext> {
    let tiresSummary: TiresSummary | null = null;
    const dossier = reactive(await this.doLoad(id)) as Dossier;
    const contactContext = await DossierContactService.loadAllForDossier(id);
    const dossierVehicle = await DossierVehicleService.loadCurrent(dossier);
    const jobContext = await JobService.loadAll(dossier, dossierVehicle.vehicle, contactContext);
    const mediaContext = await MediaFileService2.loadForDossier(dossier.id);
    const driveBookingContext = await DriveBookingService.loadForDossier(contactContext, dossier);

    // The summary load can return an error in which case we still need to show the rest of the dossier detail, so we wrap it in a try catch
    // This error state is handled in the templates
    try {
      tiresSummary = await TireClient.loadSummary(dossier.id);
    } catch (e) {}

    await ReferenceService.loadTags(TagType.DOSSIER, dossier.outletId);

    const context = reactive(new DossierContext(dossier, tiresSummary, contactContext, dossierVehicle, jobContext, mediaContext, driveBookingContext)) as DossierContext;

    await PaymentRequestService.createContext(context);

    context.freeHooks.push(
      this.startStatusUpdateListener(dossier),
      this.startGeneralUpdateListener(context),
      serviceEvents.on(DmsImportFinished, async e => {
        if (e.dossierId === context.dossier.id) context.dossier = await this.doLoad(e.dossierId);
      }),
      serviceEvents.on(WorkPreparationStatusChanged, e => this.updateWorkPreparationStatus(e.dossierId, e.status, context)),
    );

    return context;
  }

  private async doLoad(id: DossierId): Promise<Dossier> {
    const dossier = await DossierClient.findById(id);
    dossier.tags.forEach(markRaw);
    return dossier;
  }

  private updateWorkPreparationStatus(id: DossierId, status: WorkPreparationStatus, context: DossierContext): void {
    if (context.dossier.id === id) context.dossier.workPreparationStatus = status;
  }

  async loadDetail(dossierContext: DossierContext): Promise<DossierDetailContext> {
    const dossier = dossierContext.dossier;

    const context = reactive(new DossierDetailContext(dossierContext)) as DossierDetailContext;

    ChecklistService.startEventListeners(context);

    [
      context.checklists,
      context.qualityControls,
      context.testDrives,
      context.workPreparationItems,
      dossierContext.fluids,
      context.surveys,
      context.media,
      context.taskContext,
      context.driveBookingContext,
    ] = await Promise.all([
      ChecklistService.loadAll(dossier),
      QualityControlClient.loadAll(dossier.id),
      TestDriveService.loadAll(dossier),
      WorkPreparationService.loadItems(dossier),
      FluidService.loadReplenished(dossier),
      SurveyService.loadAll(dossier),
      MediaFileService.loadForDossier(dossier.id),
      TaskService.loadDossier(dossier),
      DriveBookingService.loadForDossier(dossierContext.contactContext, dossier),
      ReferenceService.loadChatGroups(dossier.outletId),
    ]);

    return context;
  }

  async doLoadTireSummary(context: DossierContext): Promise<void> {
    const tiresSummary = await TireClient.loadSummary(context.dossier.id);
    if (context.tiresSummary) context.tiresSummary.updateWith(tiresSummary);
    else context.tiresSummary = tiresSummary;
  }

  async updateTags(dossier: Dossier, tags: Array<Tag>): Promise<void> {
    const newTags = await DossierClient.updateTags(dossier.id, tags);
    newTags.forEach(markRaw);
    dossier.tags = newTags;
  }

  async updateAppointment(data: AppointmentData, dossier: Dossier): Promise<void> {
    if (!data.transportIn.byCustomer) data.waitsOnPremise = false;
    if (!data.transportIn.byCustomer || data.waitsOnPremise) data.mobilitySolution = null;
    await DossierClient.updateAppointment(dossier.id, data);
    dossier.updateAppointment(data, AuthService.getCurrentUser());
  }

  async updateNote(note: string | null, dossier: Dossier): Promise<void> {
    await DossierClient.updateNote(dossier.id, note);
    dossier.note = note;
  }

  async toggleLease(dossier: Dossier): Promise<void> {
    const newValue = !dossier.isLeasing;
    await DossierClient.updateLease(dossier.id, newValue);
    dossier.isLeasing = newValue;
  }

  async toggleFluidsLevelChecked(dossier: Dossier): Promise<void> {
    await DossierClient.markFluidLevelsChecked(dossier.id, !dossier.fluidLevelsChecked);
    dossier.fluidLevelsChecked = !dossier.fluidLevelsChecked;
  }

  async toggleVisibility(dossier: Dossier): Promise<void> {
    const newValue = !dossier.isVisible;
    await DossierClient.updateVisibility(dossier.id, newValue);
    dossier.isVisible = newValue;
  }

  async updateCleaning(dossier: Dossier, data: CleaningData): Promise<void> {
    await DossierClient.updateCleaning(dossier.id, data);
    dossier.updateCleaning(data);
  }

  async updateWorkStatus(dossier: Dossier, to: WorkStatus, data?: WorkStatusData): Promise<void> {
    const from = dossier.workStatus;
    if (from === null || !WORK_STATUS_TRANSITIONS[from].includes(to)) throw new Error(`Invalid work status transition from ${from} to ${to}`);

    const dmsSync = await DossierClient.updateWorkStatus(dossier.id, to, data);

    dossier.workStatus = to;
    dossier.workOnHoldReason = data?.tag ?? null;
    await serviceEvents.fire(new DossierWorkStatusChanged(to, dossier.id, data?.jobs));

    if (dmsSync?.pusherChannel && from === WorkStatus.NOT_STARTED && to === WorkStatus.BEING_HANDLED) {
      await DmsService.processSync(`dossiers/dossier-update_status-${dossier.id}`);
      await serviceEvents.fire(new DmsImportFinished(dossier.id));
    }
  }

  async updateVehicleStatus(dossier: Dossier, to: Exclude<VehicleStatus, VehicleStatus.READY_FOR_PICKUP>): Promise<void> {
    this.ensureValidVehicleStatusTransition(dossier, to);
    await DossierClient.updateVehicleStatus(dossier.id, to);
    dossier.vehicleStatus = to;
  }

  async markVehicleCheckedIn(data: CheckInData, changedJobs: Array<Job>, dossier: Dossier, vehicle: Vehicle): Promise<void> {
    this.ensureValidVehicleStatusTransition(dossier, VehicleStatus.CHECKED_IN);
    if (data.contact && changedJobs.length > 0) {
      await JobClient.setJobStatus(data, changedJobs, dossier.id, true);
      changedJobs.forEach(j => {
        j.order.signature = parseSignature(data.signature);
        j.order.hasSignature = isPresent(data.signature);
        j.original?.updateWith(j);
      });
    }
    await DossierClient.markVehicleCheckedIn(data, dossier);
    dossier.vehicleStatus = VehicleStatus.CHECKED_IN;
    dossier.hasFluidReplenishmentApproval = data.extraJobs.includes(CheckInExtraJob.FLUIDS);
    dossier.hasConsumptionDataApproval = data.hasConsumptionDataApproval;
    const location = vehicle.location?.isEqualTo(data.position, data.locationDescription)
      ? vehicle.location
      : Location.create(data.locationDescription, data.position, moment(), AuthService.getCurrentUser());

    vehicle.update(data, location);
  }

  async markVehicleReadyForPickup(context: PaymentRequestContext, data: ReadyForPickupServiceData, jobs: Array<Job>): Promise<void> {
    const dossier = context.dossierContext.dossier;
    this.ensureValidVehicleStatusTransition(dossier, VehicleStatus.READY_FOR_PICKUP);
    const payment = data.payment.active ? {
      ...data.payment,
      attachment: data.payment.attachment?.contentId ?? null,
    } : null;
    const clientData = {
      ...data,
      jobs: data.needFollowUp ? data.jobs : [],
      payment,
    };
    await DossierClient.markVehicleReadyForPickup(dossier.id, clientData);

    if (data.payment.active && data.targets.length > 0) {
      context.requests.push(PaymentRequest.fromReadyForPickup(data));
    }

    dossier.vehicleStatus = VehicleStatus.READY_FOR_PICKUP;
    dossier.warrantyStatus = data.warranty ? WarrantyStatus.NEW : null;

    jobs.forEach(job => {
      job.needFollowUp = data.needFollowUp ? data.jobs.includes(job) : false;
    });
  }

  async markVehiclePickedUp(data: PickedUpData, changedJobs: Array<Job>, dossier: Dossier): Promise<void> {
    this.ensureValidVehicleStatusTransition(dossier, VehicleStatus.PICKED_UP);
    if (changedJobs.length > 0) {
      await JobClient.setJobStatus(data, changedJobs, dossier.id, false);
      changedJobs.forEach(j => j.original?.updateWith(j));
    }
    await DossierClient.markVehiclePickedUp(data, dossier.id);
    dossier.vehicleStatus = VehicleStatus.PICKED_UP;
  }

  private ensureValidVehicleStatusTransition(dossier: Dossier, to: VehicleStatus): void {
    const from = dossier.vehicleStatus;
    if (from === null || !VEHICLE_STATUS_TRANSITIONS[from].includes(to)) throw new Error(`Invalid vehicle status transition from ${from} to ${to}`);
  }

  private startStatusUpdateListener(dossier: Dossier): Unbinder {
    return pushService.bind(`dossiers-${dossier.id}/dossier-status-update`, (event) => {
      dossier.workStatus = event.workStatus;
      dossier.partsOrderStatus = event.partsOrderStatus;
      dossier.vehicleStatus = event.objectStatus;
    });
  }

  private startGeneralUpdateListener(context: DossierContext): Unbinder {
    return pushService.bind(`dossiers-${context.dossier.id}/dossier-update`, (event) => {
      if (event.dossierSection === 'JOBS') {
        if (event.updates) {
          event.updates.forEach((job) => serviceEvents.fire(new JobStatusUpdated(context.dossier.id, present(parseJob(job)))));
        } else {
          event.sectionIds.forEach((jobId) => serviceEvents.fire(new JobUpdated(context.dossier.id, jobId)));
        }
      } else if (event.dossierSection === 'DOSSIER') {
        context.dossier.updateWith(present(parseDossier(event.data)));
      } else if (event.dossierSection === 'OBJECT') {
        context.dossierVehicle.vehicle.updateWith(present(parseVehicle(event.data)));
      }
    });
  }

  async updateAssignment(dossier: Dossier, data: AssignmentData): Promise<void> {
    const updatedDossier = await DossierClient.updateAssignment(dossier.id, data);
    dossier.assignedUserId = updatedDossier.assignedUserId;
    dossier.assignedGroupId = updatedDossier.assignedGroupId;
  }

  async toggleInvoiceStatus(dossier: Dossier): Promise<void> {
    const isInvoiced = !dossier.isInvoiced;
    await DossierClient.updateInvoicedStatus(dossier.id, isInvoiced);
    dossier.isInvoiced = isInvoiced;
  }

  async updateWorkshopTeam(dossier: Dossier, id: WorkshopTeamId | null): Promise<void> {
    await DossierClient.updateWorkshopTeam(dossier.id, id);
    dossier.workshopTeamId = id;
  }

  async importFromDms(dossier: Dossier): Promise<void> {
    const dmsConnections = AuthService.getCurrentUser().getOutlet(dossier.outletId).dmsConnections;
    const criteria = {
      outlet: AuthService.getCurrentUser().getOutlet(dossier.outletId),
      externalReference: present(dossier.externalReference),
      dmsConnectionName: dmsConnections.length > 1 ? dossier.dmsConnectionName : undefined,
    };
    const dmsStatus = await DmsClient.searchJobs(criteria);
    await DmsService.processSyncWithStatus(dmsStatus);
    await serviceEvents.fire(new DmsImportFinished(dossier.id));
  }
}

export default new DossierService();